import { StandardErrorData } from "src/store/bugReport/types";
import { IntlMessageId, UnprefixedKeys } from "../assets/localization/types";
import { prefixes } from "../assets/localization/utils";
import { ResponseTuple } from "../globalTypes";
import { TemplateValues } from "../hooks/useFormatMsg";
import flowioast from "./flowioast";
import { isDevEnv } from "./generalUtils";
import { formatMsg } from "./intl/intlFormmaters";

type IntlIdsPerResult = {
  singular: IntlMessageId;
  plural: IntlMessageId;
};

type MassIntlIds = {
  success: IntlIdsPerResult;
  error: IntlIdsPerResult;
};

// Acceptable = má definované success i failure pro singular i plural
type AcceptableIntlBases<ID = UnprefixedKeys["Toast"]> =
  ID extends `${infer Base}.singular.success`
    ? `${Base}.singular.failure` extends UnprefixedKeys["Toast"]
      ? `${Base}.plural.success` extends UnprefixedKeys["Toast"]
        ? `${Base}.plural.failure` extends UnprefixedKeys["Toast"]
          ? Base
          : never
        : never
      : never
    : never;

const makeMassIntlIds = (intlIdBase: AcceptableIntlBases): MassIntlIds => ({
  success: {
    singular: `${prefixes.toast}.${intlIdBase}.singular.success`,
    plural: `${prefixes.toast}.${intlIdBase}.plural.success`,
  } as const,
  error: {
    singular: `${prefixes.toast}.${intlIdBase}.singular.failure`,
    plural: `${prefixes.toast}.${intlIdBase}.plural.failure`,
  } as const,
});

export const makeErrorToastMsg = (
  generalMsg: string,
  errorSpecific?: string | string[]
) => {
  if (!errorSpecific || errorSpecific?.length === 0) return generalMsg;

  const appendSpecific = (base: string, toAppend: string) =>
    base.concat(`\n\n> `).concat(toAppend);

  if (typeof errorSpecific === "string") {
    return appendSpecific(generalMsg, errorSpecific);
  }

  return generalMsg + errorSpecific.reduce(appendSpecific, "");
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMessageFromError = (error: any) => {
  if (!error) return undefined;
  const stringResponse = typeof error?.response?.data === "string";
  const isAspError = typeof error?.response?.data?.errors === "object";

  if (stringResponse) return error.response.data as string;

  if (isAspError && isDevEnv && error.response) {
    const { title, errors } = error.response.data;
    const allErrorMsgs = Object.values(errors).flat().join("; ");
    return `${title}: ${allErrorMsgs}`;
  }

  return error.message;
};

type Config = {
  intlParams?: TemplateValues;
  ignoreSuccess?: boolean;
  ignoreFailure?: boolean;
};

const defaultConfig: Config = { ignoreSuccess: false, ignoreFailure: false };

type TRT = (
  tuples: ResponseTuple[],
  intlIdBase: AcceptableIntlBases,
  intlParams?: TemplateValues
) => void;
export const toastifyResponseTuples: TRT = (tuples, intlIdBase, config) => {
  const combinedConfig: Config = { ...defaultConfig, ...config };
  const { ignoreFailure, ignoreSuccess, intlParams } = combinedConfig;

  const successTuples = tuples.filter((t) => t[1] === null);
  const errorTuples = tuples.filter((t) => t[1] !== null);

  const handleSuccessTuples = (intlIds: IntlIdsPerResult) => {
    if (ignoreSuccess) return;
    const count = successTuples.length;
    if (count === 0) return;
    const activeIntlId = count === 1 ? intlIds.singular : intlIds.plural;

    flowioast.success.fromIntlId(activeIntlId, { ...intlParams, count });
  };

  const handleErrorTuples = (intlIds: IntlIdsPerResult) => {
    if (ignoreFailure) return;
    const count = errorTuples.length;
    if (count === 0) return;
    const activeIntlId = count === 1 ? intlIds.singular : intlIds.plural;
    const errors = errorTuples.map((t) => t[1] ?? ({} as StandardErrorData));

    const baseMsg = formatMsg(activeIntlId, { ...intlParams, count });
    const errorMsgs = errors.map(getMessageFromError);
    const fullMsg = makeErrorToastMsg(baseMsg, errorMsgs);

    flowioast.error(fullMsg, errors);
  };

  const massIntlIds = makeMassIntlIds(intlIdBase);

  handleSuccessTuples(massIntlIds.success);
  handleErrorTuples(massIntlIds.error);
};

export const getMessagefromBlob = async (e: {
  [x: string]: unknown;
  response: {
    [x: string]: unknown;
    data: Blob;
  };
}) => {
  const blob = new Blob([e.response.data]);
  const text = await blob.text();

  return {
    ...e,
    response: {
      ...e.response,
      data: text,
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toastifyResponseByStatus = (response: any) => {
  if (response && "resultState" in response.data) {
    const { resultState, message } = response.data;
    flowioast[
      resultState as "error" | "warning" | "success"
    ].fromResponseMessage(message, {
      message,
      name: `common.${resultState}`,
      stack: message,
    });
  }
};
